import { Box, BoxProps } from '@withjoy/joykit';
import { ChevronLeftSquare, ChevronRightSquare } from '@withjoy/joykit/icons';
import React, { MouseEventHandler, useEffect, useRef, useState } from 'react';
import Slider, { Settings } from 'react-slick';
import { Container, DotsContainer, DotsTrack, Dot, FilledNavButton, MinimalNavButton, StyledImage } from './PhotoCarousel.styles';
import { DEFAULT_HOTEL_IMAGE } from '@shared/utils/constants';

type NavigationVariant = 'filled' | 'minimal';
type NavigationVisibility = 'default' | 'hover';

interface CarouselButtonProps {
  onClick: MouseEventHandler;
  side: 'left' | 'right';
  variant?: NavigationVariant;
}

const navigationConfig = {
  Icons: {
    left: ChevronLeftSquare,
    right: ChevronRightSquare,
  },
  filled: {
    Component: FilledNavButton,
    IconSize: 24,
  },
  minimal: {
    Component: MinimalNavButton,
    IconSize: 16,
  },
} as const;

const NavigationButton = ({ onClick, side, variant = 'filled' }: CarouselButtonProps) => {
  const config = navigationConfig[variant];
  const Icon = navigationConfig.Icons[side];
  const ButtonComponent = config.Component;
  const iconSize = config.IconSize;

  return (
    <ButtonComponent
      onClick={onClick}
      side={side}
    >
      <Icon size={iconSize} />
    </ButtonComponent>
  );
};

export function PhotoCarousel({
  images,
  aspectRatio,
  navigation = { variant: 'filled', visible: 'hover' },
  imgProps,
  ...restProps
}: {
  images: Array<{ src: string; alt: string }>;
  aspectRatio?: string;
  navigation?: {
    variant: NavigationVariant;
    visible: NavigationVisibility;
  };
  imgProps?: BoxProps<'img'>;
} & BoxProps) {
  const sliderRef = useRef<Slider>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imageSources, setImageSources] = useState<Array<string>>(images.map(img => img.src?.trim() || DEFAULT_HOTEL_IMAGE));
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set());
  const [sliderKey, setSliderKey] = useState(0);

  const handleImageError = (index: number) => {
    if (!failedImages.has(index)) {
      setFailedImages(prev => {
        const newSet = new Set(prev);
        newSet.add(index);
        return newSet;
      });
      setImageSources(prev => {
        const updated = [...prev];
        updated[index] = DEFAULT_HOTEL_IMAGE;
        return updated;
      });
    }
  };

  useEffect(() => {
    setImageSources(images.map(img => img.src?.trim() || DEFAULT_HOTEL_IMAGE));
    setFailedImages(new Set());
    setCurrentSlide(0);
    setSliderKey(prev => prev + 1);
  }, [images]);

  const handleNextClick: MouseEventHandler = e => {
    e.preventDefault();
    e.stopPropagation();
    sliderRef.current?.slickNext();
  };

  const handlePrevClick: MouseEventHandler = e => {
    e.preventDefault();
    e.stopPropagation();
    sliderRef.current?.slickPrev();
  };

  const handleDotClick: MouseEventHandler = e => {
    e.preventDefault();
    e.stopPropagation();
    const index = parseInt(e.currentTarget.getAttribute('data-index') || '0', 10);
    sliderRef.current?.slickGoTo(index);
  };

  // Calculate the offset for the dots track
  const calculateDotsOffset = () => {
    const dotWidth = 6; // width of each dot in px
    const dotGap = 8; // gap between dots in px
    const visibleDotsCount = 5;
    const totalWidth = dotWidth + dotGap; // width of one dot + gap

    // Calculate the center position (where the middle dot should be)
    const centerIndex = Math.floor(visibleDotsCount / 2);

    // Calculate how many positions we need to move from the center
    const offset = -(currentSlide - centerIndex) * totalWidth;

    // Clamp the offset to prevent over-scrolling at the start and end
    const maxOffset = 0;
    const minOffset = -((images.length - visibleDotsCount) * totalWidth);

    return Math.min(maxOffset, Math.max(minOffset, offset));
  };

  const settings: Settings = {
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    beforeChange: (_, next) => setCurrentSlide(next),
    lazyLoad: 'ondemand',
  };

  return (
    <Box
      position="relative"
      width="100%"
      height="100%"
      overflow="hidden"
      {...restProps}
    >
      <Container
        position="absolute"
        inset={0}
        visible={navigation.visible}
        hideSlider={images.length === 1}
      >
        <Slider
          key={sliderKey}
          {...settings}
          ref={sliderRef}
        >
          {images.map((image, index) => (
            <Box
              key={index}
              width="100%"
              height="100%"
            >
              <StyledImage
                as="img"
                {...imgProps}
                alt={image.alt}
                aspectRatio={aspectRatio}
                src={imageSources[index]}
                onError={() => handleImageError(index)}
              />
            </Box>
          ))}
        </Slider>
        {images.length > 1 && (
          <>
            {currentSlide > 0 && (
              <NavigationButton
                onClick={handlePrevClick}
                side="left"
                variant={navigation.variant}
              />
            )}
            {currentSlide < images.length - 1 && (
              <NavigationButton
                onClick={handleNextClick}
                side="right"
                variant={navigation.variant}
              />
            )}
            <DotsContainer>
              <DotsTrack offset={calculateDotsOffset()}>
                {images.map((_, index) => (
                  <Dot
                    key={index}
                    active={index === currentSlide}
                    data-index={index}
                    onClick={handleDotClick}
                  />
                ))}
              </DotsTrack>
            </DotsContainer>
          </>
        )}
      </Container>
    </Box>
  );
}
